import type { UrlTargeting } from '@openx/types/targeting/domains.ts';
import type { TargetingReadOnlyProps } from '@openx/types/targeting/targeting.ts';
import { noop } from '@openx/utils/noop.ts';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState.ts';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen.ts';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown.ts';
import { TargetingDropdownSection } from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection.tsx';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly.tsx';
import { DomainsItems } from './DomainsItems.tsx';

type InfoComponentProps = { infoComponent: JSX.Element | null; userChangedInitialValue?: boolean; field: string };

export const DomainsReadOnly = ({
  name,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  infoComponent,
  userChangedInitialValue,
  accountUid,
  field,
}: TargetingReadOnlyProps<UrlTargeting> & InfoComponentProps) => {
  const { isDisabledOrHasError, disabledReasonMessage, errorReasonMessage } = useTargetingDisabledState(field);

  const { t } = useTranslation();

  const hasConfiguredDomains = useMemo(() => {
    if (!targetingParams) return false;

    const hasNonEmptyUrl = (targetingParams.urls ?? []).some(url => String(url).trim().length > 0);
    const hasInventoryList = String(targetingParams.inventorylist_id ?? '').trim().length > 0;

    return hasNonEmptyUrl || hasInventoryList;
  }, [targetingParams]);

  const optionsCount = hasConfiguredDomains ? 1 : 0;
  const hasValue = optionsCount > 0;

  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);
  const guardedToggleOpen = hasValue ? toggleOpen : noop;

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        isEmpty={!hasConfiguredDomains}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabledOrHasError}
        description={t(
          'Narrow delivery to specific websites either selected from a predefined list or entered manually for custom control.'
        )}
        isSingleChoice
        optionsCount={optionsCount}
        isOpen={isOpen}
        toggleOpen={guardedToggleOpen}
        userChangedInitialValue={userChangedInitialValue}
        field={field}
        disabledReasonMessage={disabledReasonMessage}
      >
        {infoComponent}
        {hasConfiguredDomains && !!targetingParams && isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <TargetingDropdownSection
              name={name}
              isEmpty={false}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              isDisabled={isDisabledOrHasError}
              hasOptionsSelected={!!optionsCount && optionsCount > 0}
              disabledReasonMessage={disabledReasonMessage}
            >
              <DomainsItems
                targetingParams={targetingParams}
                accountUid={accountUid}
                disabledReasonMessage={errorReasonMessage}
              />
            </TargetingDropdownSection>
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      userChangedInitialValue={userChangedInitialValue}
      isEmpty={!hasConfiguredDomains}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabledOrHasError}
      description={t(
        'Narrow delivery to specific websites either selected from a predefined list or entered manually for custom control.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {infoComponent}
      {hasConfiguredDomains && targetingParams && (
        <DomainsItems
          targetingParams={targetingParams}
          accountUid={accountUid}
          disabledReasonMessage={errorReasonMessage}
        />
      )}
    </TargetingReadOnly>
  );
};
